import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { FaArrowLeft, FaEdit, FaTrash, FaCheckCircle, FaTimesCircle, FaUsers, FaCalendarAlt } from 'react-icons/fa';
import api from '../../utils/api.service';
import { ENDPOINTS } from '../../utils/api-config';
import { toast } from 'react-toastify';
import './Services.css';

const ServiceDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [service, setService] = useState(null);

  useEffect(() => {
    const fetchService = async () => {
      try {
        const response = await api.get(`${ENDPOINTS.SERVICES.base}/${id}`);
        setService(response.data);
      } catch (error) {
        console.error('Failed to fetch service:', error);
        toast.error('Failed to load service details');
        navigate('/services');
      } finally {
        setLoading(false);
      }
    };

    fetchService();
  }, [id, navigate]);

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      try {
        await api.delete(`${ENDPOINTS.SERVICES.base}/${id}`);
        toast.success('Service deleted successfully');
        navigate('/services');
      } catch (error) {
        console.error('Failed to delete service:', error);
        toast.error('Failed to delete service');
      }
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loader"></div>
        <p>Loading service details...</p>
      </div>
    );
  }

  if (!service) return null;

  return (
    <div className="service-details-page">
      <div className="page-header">
        <button onClick={() => navigate('/services')} className="btn btn-secondary">
          <FaArrowLeft /> Back to Services
        </button>
        <div className="page-actions">
          <Link to={`/services/${id}/edit`} className="btn btn-primary">
            <FaEdit /> Edit Service
          </Link>
          <button className="btn btn-danger" onClick={handleDelete}>
            <FaTrash /> Delete
          </button>
        </div>
      </div>

      <div className="service-header-card content-card">
        <div className="service-main-info">
          <div className="service-badge-row">
            <span className="service-category-badge">{service.category?.name || (typeof service.category === 'string' ? service.category : 'Uncategorized')}</span>
            {service.isActive ? (
              <span className="status-badge approved">Active</span>
            ) : (
              <span className="status-badge rejected">Inactive</span>
            )}
            {service.isPopular && <span className="status-badge popular">Popular</span>}
          </div>
          <h1>{service.name}</h1>
          <p className="service-description-large">{service.description}</p>
        </div>
      </div>

      <div className="details-grid">
        <div className="content-card">
          <h3 className="card-title">Service Parameters</h3>
          <div className="info-list">
            <div className="info-item">
              <label>Base Price</label>
              <span style={{ textTransform: 'capitalize' }}>
                ${service.basePrice} / {service.pricingType || 'hour'}
              </span>
            </div>
            <div className="info-item">
              <label>Duration / Package</label>
              <span>
                {service.pricingType === 'monthly' ? '30 Days (Full Month)' : 
                 service.pricingType === 'weekly' ? '7 Days (Full Week)' :
                 service.pricingType === 'daily' ? '24 Hours (Full Day)' :
                 `${service.durationMinutes || 0} Minutes`}
              </span>
            </div>
            {/* <div className="info-item">
              <label>Created By</label>
              <span className="fw-bold text-primary">
                {service.creator ? (
                  <Link to={`/providers/${service.creator.id}`} className="service-details-link">
                    {service.creator.firstName} {service.creator.lastName}
                  </Link>
                ) : 'System Admin'}
              </span>
            </div> */}
            <div className="info-item">
              <label>Created At</label>
              <span>{new Date(service.createdAt).toLocaleString()}</span>
            </div>
            <div className="info-item">
              <label>Last Updated</label>
              <span>{new Date(service.updatedAt).toLocaleString()}</span>
            </div>
          </div>
        </div>

        <div className="content-card">
          <h3 className="card-title">Performance Stats</h3>
          <div className="stats-mini-grid">
            <div className="stat-box">
              <FaUsers className="stat-icon" />
              <div className="stat-info">
                <h4>{service.providers?.length || 0}</h4>
                <p>Providers</p>
              </div>
            </div>
            <div className="stat-box">
              <FaCalendarAlt className="stat-icon" />
              <div className="stat-info">
                <h4>{service.bookings?.length || 0}</h4>
                <p>Total Bookings</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="content-card" style={{ marginTop: '24px' }}>
        <h3 className="card-title">Authorized Provider</h3>
        { (service.creator || (service.providers && service.providers.length > 0)) ? (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Provider</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(service.creator ? [service.creator] : service.providers).map(provider => (
                  <tr key={provider.id}>
                    <td>{provider.firstName} {provider.lastName} {service.creator && <span className="badge approved ms-2">Author</span>}</td>
                    <td>{provider.email}</td>
                    <td>
                      <span className={`status-badge ${provider.status}`}>
                        {typeof provider.status === 'string' ? provider.status : 'Unknown'}
                      </span>
                    </td>
                    <td>
                      <Link to={`/providers/${provider.id}`} className="btn-link">View Profile</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="empty-state">No provider is currently authorized for this service.</p>
        )}
      </div>

      <div className="content-card" style={{ marginTop: '24px' }}>
        <h3 className="card-title">Recent Service History & Customers</h3>
        {service.bookings && service.bookings.length > 0 ? (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Booking #</th>
                  <th>Customer</th>
                  <th>Provider</th>
                  <th>Scheduled Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {[...service.bookings].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map(booking => (
                  <tr key={booking.id}>
                    <td><span className="transaction-id">{booking.bookingNumber}</span></td>
                    <td>
                      <Link to={`/customers/${booking.customer?.id}`} className="service-details-link">
                        {booking.customer?.firstName} {booking.customer?.lastName}
                      </Link>
                    </td>
                    <td>
                      <Link to={`/providers/${booking.provider?.id}`} className="service-details-link">
                        {booking.provider?.firstName} {booking.provider?.lastName}
                      </Link>
                    </td>
                    <td>{new Date(booking.scheduledDate).toLocaleDateString()}</td>
                    <td>
                      <span className={`status-badge ${booking.status?.toLowerCase()}`}>
                        {booking.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="empty-state">No bookings have been made for this service yet.</p>
        )}
      </div>
    </div>
  );
};

export default ServiceDetails;
